import styled from "@emotion/styled";
import { AnimatePresence, LayoutGroup, motion, PanInfo } from "framer-motion";
import React from "react";
import { API } from "../../API";
import { FcmPushNotification } from "../../messages";
import { notificationSound } from "../../utils/Sounds";
import SafeArea from "../Generic/SafeArea";
import { ToastsOverlay } from "../Generic/ToastsOverlay";
import PushNotificationsListener from "../PushNotificationsListener";
import InAppNotification from "./InAppNotification";

type Props = {
  api: API;
  onClickPushNotification: (data: FcmPushNotification) => void;
  onUpdatePushNotificationStatus: (enabled: boolean) => void;
};

export type Notification = { message: string; data: FcmPushNotification | null };

const InAppNotifications = (props: Props) => {
  const [notifications, setNotifications] = React.useState<Notification[]>([]);

  const handleClick = (notification: Notification) => {
    setNotifications((notifications) => notifications.filter((n) => n !== notification));

    if (notification.data !== null) {
      props.onClickPushNotification(notification.data);
    }
  };

  const handleAnimationComplete = (notification: Notification) => {
    setNotifications((notifications) => notifications.filter((n) => n !== notification));
  };

  const handlePan = (info: PanInfo, notification: Notification) => {
    if (info.offset.y < -20) {
      handleAnimationComplete(notification);
      return;
    }
  };

  return (
    <>
      <Root>
        <SafeArea top={true}>
          <NotificationsGroup>
            <LayoutGroup>
              <AnimatePresence>
                {notifications.map((notification) => (
                  <motion.div
                    layout
                    key={`${notification.message}-${notification.data?.id}`}
                    initial={{ y: -92, scale: 0.95 }}
                    animate={{ y: 0, scale: 1 }}
                    exit={{ y: -92, scale: 0.95, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 500, damping: 30, duration: 0.5 }}
                    drag="y"
                    dragConstraints={{ top: 0, bottom: 0 }}
                    dragElastic={{ top: 0.5, bottom: 0.1 }}
                    onPanEnd={(_e, info) => handlePan(info, notification)}
                  >
                    <InAppNotification
                      notification={notification}
                      onAnimationComplete={handleAnimationComplete}
                      onClick={handleClick}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </LayoutGroup>
          </NotificationsGroup>
        </SafeArea>
      </Root>

      <PushNotificationsListener
        api={props.api}
        onUpdatePushNotificationStatus={props.onUpdatePushNotificationStatus}
        onClickPushNotification={props.onClickPushNotification}
        onRequestDisplayNotificationBanner={(message, data) => {
          notificationSound.play();
          setNotifications((notifications) => [...notifications, { message, data }]);
        }}
      />
    </>
  );
};

const Root = styled(ToastsOverlay)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
`;

const NotificationsGroup = styled(motion.div)`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export default React.memo(InAppNotifications);
